import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Loader2, Rocket, Radio } from 'lucide-react';
import { API, errText } from '../api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/public/Header';

export const money = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export function TermToggle({ terms, term, onChange }) {
  return (
    <div className="inline-flex rounded-full border hairline p-1 bg-panel2/40" data-testid="term-toggle">
      {Object.entries(terms).map(([id, t]) => (
        <button key={id} onClick={() => onChange(id)} data-testid={`term-${id}`} className={`rounded-full px-4 py-2 text-xs sm:text-sm transition-colors ${term === id ? 'bg-gold text-ink' : 'text-cream/65 hover:text-cream'}`}>
          {t.name}{t.discount ? <span className={`ml-1.5 text-[10px] font-semibold ${term === id ? 'opacity-80' : 'text-gold'}`}>Save {t.discount}%</span> : null}
        </button>
      ))}
    </div>
  );
}

export function TierCard({ id, tier, quote, term, terms, onPick, busy, cta = 'Start this plan', setupFee = 399 }) {
  return (
    <div className={`relative rounded-3xl border p-7 flex flex-col ${tier.popular ? 'border-gold/60 bg-gold/[0.05]' : 'hairline bg-panel/60'}`} data-testid={`tier-card-${id}`}>
      {tier.popular && <span className="absolute -top-3 left-7 text-[10px] tracking-widest uppercase bg-gold text-ink rounded-full px-3 py-1">Most popular</span>}
      <p className="font-display text-3xl text-cream">{tier.name}</p>
      <p className="text-sm text-cream/60 mt-1">{tier.tagline}</p>
      <div className="mt-5">
        <p className="font-display text-5xl text-cream" data-testid={`tier-price-${id}`}>
          {quote.discount ? <span className="text-2xl text-cream/40 line-through mr-2 font-sans" data-testid={`tier-regular-${id}`}>{money(tier.monthly)}</span> : null}
          {money(quote.per_month)}<span className="text-lg text-cream/50 font-sans">/mo</span>
        </p>
        <p className="text-xs text-cream/55 mt-2">Equals {money(quote.per_month)} per month &middot; {money(quote.total)} billed {terms[term].name.toLowerCase()} ({quote.months} months)</p>
        {quote.discount ? (
          <p className="text-sm font-semibold text-gold mt-1.5" data-testid={`tier-savings-${id}`}>
            You save {money(tier.monthly * quote.months - quote.total)} ({quote.discount}% off the {money(tier.monthly)}/mo quarterly rate){quote.setup_fee ? '' : ` + ${money(setupFee)} setup fee waived`}
          </p>
        ) : null}
        <p className={`text-sm mt-1.5 ${quote.setup_fee ? 'text-cream/80' : 'text-gold font-semibold'}`} data-testid={`tier-setup-${id}`}>
          {quote.setup_fee ? `+ ${money(quote.setup_fee)} one-time setup fee` : 'No setup fee on annual'}
        </p>
      </div>
      <ul className="mt-6 space-y-2.5 flex-1">
        {tier.features.map((f) => <li key={f} className="flex gap-2.5 text-sm text-cream/75"><Check size={15} className="text-gold shrink-0 mt-0.5" /> {f}</li>)}
      </ul>
      <button onClick={() => onPick(id)} disabled={busy} data-testid={`tier-pick-${id}`} className={`mt-7 rounded-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50 ${tier.popular ? 'btn-gold' : 'btn-ghost'}`}>
        {busy === id ? <Loader2 size={15} className="animate-spin" /> : null} {cta} · {money(quote.due_today)} today
      </button>
    </div>
  );
}

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [term, setTerm] = useState('quarterly');
  const [busy, setBusy] = useState(null);
  useEffect(() => { API.plans().then(setPlans).catch(() => {}); }, []);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('term')) setTerm(q.get('term'));
  }, []);

  const pick = async (tier) => {
    if (!user) { nav(`/signin?next=${encodeURIComponent(`/pricing?plan=${tier}&term=${term}`)}`); return; }
    if (user.role === 'studio') { toast.error('Sign in as a customer to subscribe.'); return; }
    setBusy(tier);
    try { const { checkout_url } = await API.checkout({ kind: 'subscription', tier, term }); window.location.assign(checkout_url); }
    catch (e) { toast.error(errText(e, 'Could not start checkout.')); setBusy(null); }
  };

  if (!plans) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-gold" /></div>;
  const lp = plans.launchpad;
  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <main className="max-w-6xl mx-auto px-5 pt-32 pb-24" data-testid="pricing-page">
        <p className="eyebrow mb-4">Pricing</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-cream leading-[0.95] max-w-3xl">Your song, <span className="italic text-gold-grad">launched</span> and kept in rotation.</h1>
        <p className="mt-6 text-cream/65 text-base md:text-lg max-w-2xl">Two ways to get your Brand Song in front of people: a one-time Launch Pad for a big first splash, or ongoing Social Media Management so your brand is heard every week.</p>

        <section className="mt-20" id="launchpad">
          <p className="flex items-center gap-2 eyebrow mb-3"><Rocket size={14} className="text-gold" /> Social Media Launch Pad &middot; one-time, per song</p>
          <h2 className="text-base md:text-lg text-cream/80 max-w-2xl">Ready to post everywhere within 48 hours of buying your song. Bought from your dashboard once your song is yours.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {Object.entries(lp).map(([id, p]) => (
              <div key={id} className={`rounded-3xl border p-7 ${id === 'pro' ? 'border-gold/60 bg-gold/[0.05]' : 'hairline bg-panel/60'}`} data-testid={`launchpad-card-${id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <p className="font-display text-3xl text-cream">{p.name}</p>
                  <p className="font-display text-4xl text-cream">{money(p.price)}</p>
                </div>
                <p className="text-xs text-cream/50 mt-1">Each additional song {money(p.extra_song)}</p>
                <ul className="mt-5 space-y-2.5">{p.features.map((f) => <li key={f} className="flex gap-2.5 text-sm text-cream/75"><Check size={15} className="text-gold shrink-0 mt-0.5" /> {f}</li>)}</ul>
              </div>
            ))}
          </div>
          <p className="text-xs text-cream/45 mt-4">Buy all three versions of your song and the Launch Pad is on us. Prices before sales tax, calculated at checkout.</p>
        </section>

        <section className="mt-24" id="management">
          <p className="flex items-center gap-2 eyebrow mb-3"><Radio size={14} className="text-gold" /> Social Media Management &middot; subscription</p>
          <h2 className="text-base md:text-lg text-cream/80 max-w-2xl">We build every post around your song — clips with new visuals, customer stories, seasonal versions — you approve each one in your dashboard, and we post it on your accounts.</h2>
          <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
            <TermToggle terms={plans.terms} term={term} onChange={setTerm} />
            <div className="text-sm text-cream/80 max-w-md" data-testid="setup-fee-note">
              <p>Billed quarterly at minimum &mdash; no monthly plan. Pay semi-annually to <span className="text-gold font-semibold">save 10%</span>, or annually to <span className="text-gold font-semibold">save 15%</span>.</p>
              <p className="mt-1 text-gold font-semibold">One-time setup fee {money(plans.setup_fee)} on quarterly and semi-annual plans &middot; waived on annual.</p>
              <p className="mt-1 text-xs text-cream/55" data-testid="tax-note">All prices are before sales tax. Tax is calculated at checkout based on your billing address and is included in each renewal charge.</p>
            </div>
          </div>
          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            {Object.entries(plans.tiers).map(([id, t]) => <TierCard key={id} id={id} tier={t} quote={plans.matrix[id][term]} term={term} terms={plans.terms} onPick={pick} busy={busy} setupFee={plans.setup_fee} />)}
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6 text-sm text-cream/65">
            <div><p className="text-cream mb-1">What setup includes</p>Brand voice & audience profile, account audit and clean-up, 30-day launch calendar, hashtag bank, six branded templates, posting schedule.</div>
            <div><p className="text-cream mb-1">How posting works</p>You give us access through Meta Business Suite / TikTok (never a password). Every post lands in your dashboard for approval before it goes live.</div>
            <div><p className="text-cream mb-1">Terms</p>Billed up front per term. Cancel anytime after your current term; prepaid terms can be paused once a year. Price locked for the term.</div>
          </div>
        </section>
      </main>
    </div>
  );
}
